<script lang="ts">
	/**
	 * Captura de assinatura em <canvas>, com Pointer Events — o mesmo código
	 * serve dedo, stylus e rato, que é o que o tablet do delegado exige.
	 *
	 * Desenha em resolução física (devicePixelRatio) para o traço não sair
	 * pixelizado no PDF; exporta PNG com fundo transparente.
	 */

	import type { Idioma } from '@ac/shared';

	const TEXTOS = {
		pt: { aqui: 'Assine aqui', limpar: 'Limpar assinatura', area: 'Área de assinatura' },
		es: { aqui: 'Firme aquí', limpar: 'Borrar firma', area: 'Área de firma' }
	} as const;

	interface Props {
		/** Chamado a cada alteração: PNG em data URL, ou null se estiver vazio. */
		onchange?: (png: string | null) => void;
		altura?: number;
		desativado?: boolean;
		/** Segue a clínica, como tudo o resto. */
		idioma?: Idioma;
	}

	let { onchange, altura = 200, desativado = false, idioma = 'pt' }: Props = $props();
	const t = $derived(TEXTOS[idioma] ?? TEXTOS.pt);

	let canvas = $state<HTMLCanvasElement | null>(null);
	let desenhando = false;
	let temTraco = $state(false);
	/** Conta os pontos: dois toques acidentais não são uma assinatura. */
	let pontos = 0;

	function contexto(): CanvasRenderingContext2D | null {
		return canvas?.getContext('2d') ?? null;
	}

	/** Redimensiona para o tamanho físico sem perder o traço já desenhado. */
	function ajustaResolucao() {
		if (!canvas) return;
		const ctx = contexto();
		if (!ctx) return;

		const dpr = window.devicePixelRatio || 1;
		const caixa = canvas.getBoundingClientRect();
		const largura = Math.round(caixa.width * dpr);
		const alturaFisica = Math.round(caixa.height * dpr);
		if (canvas.width === largura && canvas.height === alturaFisica) return;

		const anterior = temTraco ? canvas.toDataURL('image/png') : null;
		canvas.width = largura;
		canvas.height = alturaFisica;

		ctx.scale(dpr, dpr);
		ctx.lineWidth = 2.2;
		ctx.lineCap = 'round';
		ctx.lineJoin = 'round';
		ctx.strokeStyle = '#111827';

		if (anterior) {
			const img = new Image();
			img.onload = () => ctx.drawImage(img, 0, 0, caixa.width, caixa.height);
			img.src = anterior;
		}
	}

	$effect(() => {
		if (!canvas) return;
		ajustaResolucao();
		const observador = new ResizeObserver(() => ajustaResolucao());
		observador.observe(canvas);
		return () => observador.disconnect();
	});

	function posicao(evento: PointerEvent): { x: number; y: number } {
		const caixa = canvas!.getBoundingClientRect();
		return { x: evento.clientX - caixa.left, y: evento.clientY - caixa.top };
	}

	function comecar(evento: PointerEvent) {
		if (desativado || !canvas) return;
		evento.preventDefault();
		canvas.setPointerCapture(evento.pointerId);
		desenhando = true;
		pontos = 1;

		const ctx = contexto();
		const { x, y } = posicao(evento);
		ctx?.beginPath();
		ctx?.moveTo(x, y);
	}

	function mover(evento: PointerEvent) {
		if (!desenhando || desativado) return;
		evento.preventDefault();

		const ctx = contexto();
		if (!ctx) return;

		// coalesced events: em stylus a 120Hz isto é a diferença entre um traço
		// suave e uma linha aos degraus.
		const eventos = evento.getCoalescedEvents?.() ?? [evento];
		for (const e of eventos) {
			const { x, y } = posicao(e);
			ctx.lineTo(x, y);
			pontos++;
		}
		ctx.stroke();

		if (!temTraco && pontos > 4) temTraco = true;
	}

	function terminar(evento: PointerEvent) {
		if (!desenhando) return;
		desenhando = false;
		canvas?.releasePointerCapture(evento.pointerId);
		emite();
	}

	function emite() {
		if (!canvas) return;
		onchange?.(temTraco ? canvas.toDataURL('image/png') : null);
	}

	export function limpar() {
		const ctx = contexto();
		if (!ctx || !canvas) return;
		const dpr = window.devicePixelRatio || 1;
		ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
		temTraco = false;
		pontos = 0;
		emite();
	}

	export function estaPreenchido(): boolean {
		return temTraco;
	}
</script>

<div class="flex flex-col gap-2">
	<div
		class="relative rounded-xl border-2 border-dashed border-slate-300 bg-white
		       {desativado ? 'opacity-50' : ''}"
		style="height: {altura}px"
	>
		<canvas
			bind:this={canvas}
			onpointerdown={comecar}
			onpointermove={mover}
			onpointerup={terminar}
			onpointercancel={terminar}
			onpointerleave={terminar}
			class="h-full w-full touch-none {desativado ? 'cursor-not-allowed' : 'cursor-crosshair'}"
			aria-label={t.area}
		></canvas>

		{#if !temTraco}
			<p
				class="pointer-events-none absolute inset-0 flex items-center justify-center
				       text-sm text-slate-400"
			>
				{t.aqui}
			</p>
		{/if}
	</div>

	<div class="flex justify-end">
		<button
			type="button"
			onclick={limpar}
			disabled={!temTraco || desativado}
			class="text-sm text-slate-500 underline underline-offset-2
			       disabled:cursor-not-allowed disabled:opacity-40 hover:text-slate-800"
		>
			{t.limpar}
		</button>
	</div>
</div>
